import { DOCUMENT, isPlatformBrowser } from '@angular/common'
import { HttpInterceptorFn } from '@angular/common/http'
import { PLATFORM_ID, inject } from '@angular/core'

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])
const XSRF_COOKIE = 'XSRF-TOKEN'
const XSRF_HEADER = 'X-XSRF-TOKEN'

/**
 * Belt-and-suspenders for Spring CSRF double-submit.
 *
 * Angular's built-in HttpXsrfInterceptor skips absolute http(s) URLs. When
 * ecomBffBasePath was baked as https://ecom-beta.../api/bff, checkout/commit
 * sent the XSRF-TOKEN cookie but not X-XSRF-TOKEN → 403. Relative /api/bff
 * is the primary fix; this interceptor still attaches the header for same-origin
 * absolute URLs so a future absolute bake cannot break commit again.
 */
const readCookie = (doc: Document, name: string): string | null => {
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')
  const match = doc.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

const isSameOriginOrRelative = (url: string, origin: string): boolean => {
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return true
  }
  try {
    return new URL(url).origin === origin
  } catch {
    return false
  }
}

export const xsrfSameOriginInterceptor: HttpInterceptorFn = (request, next) => {
  const platformId = inject(PLATFORM_ID)
  if (!isPlatformBrowser(platformId)) {
    return next(request)
  }
  if (!MUTATING_METHODS.has(request.method.toUpperCase())) {
    return next(request)
  }
  if (request.headers.has(XSRF_HEADER)) {
    return next(request)
  }

  const doc = inject(DOCUMENT)
  const origin = doc.defaultView?.location?.origin ?? ''
  if (!isSameOriginOrRelative(request.url, origin)) {
    return next(request)
  }

  const token = readCookie(doc, XSRF_COOKIE)
  if (!token) {
    return next(request)
  }

  return next(request.clone({ setHeaders: { [XSRF_HEADER]: token } }))
}
